import { Card as SdsCard } from '@s-group/design-system-components/Card/Card'
import { HeadingProps } from '@s-group/design-system-components/Heading/Heading'
import { Link as SdsLink } from '@s-group/design-system-components/Link/Link'
import { JSX, ReactElement, ReactNode, useId } from 'react'
import { styled } from 'styled-components'

import { PrimaryButton } from '../actions/special/DynamicWidthButtons'
import {
  BankingAccountIcon,
  BankingCredentialsIcon,
  BankingPayslipIcon,
  BankingStockIcon,
  CommunicationMessageUnreadIcon,
  DocumentEditIcon,
  DocumentHistoryIcon,
  DocumentTextIcon,
  HospitalityDiamondIcon,
  IconSize,
  NavigationChevronRightIcon,
  PaymentSBonusVerticalIcon,
  PaymentWalletIcon,
  PenIcon,
  SokGreenIcon,
  UserPasswordIcon,
  UserProfileIcon,
} from '../assets/Icons'
import { ColoredLabelProps } from '../dataDisplay/ColoredLabel'
import { Divider } from '../decorative/Divider'
import { Colors } from '../global/colors'
import { BREAKPOINT_DESKTOP_PX } from '../global/constants'
import { SPACE_SMALL, SPACE_XXSMALL } from '../global/spacing'
import { QuaternaryHeading } from '../typography/headings/QuaternaryHeading'
import { resolveCssDimensionValue } from '../utilities/helpers'

export type CardProps = BasicCardProps | PrimaryCardProps | RichCardProps

export type BasicCardProps = {
  variant: 'basic'
} & BaseCardProps

export type PrimaryCardProps = {
  variant: 'primary'
  clickOptions: ClickOptions
} & BaseCardProps

export type RichCardProps = {
  variant: 'rich'
  shortcutElements: ReactElement[]
  clickOptions?: ClickOptions
} & BaseCardProps

type CardTagName = keyof Pick<JSX.IntrinsicElements, 'article' | 'section' | 'li'>

type TitleProps =
  | {
      title: CardTitle
      ['aria-labelledby']?: string
    }
  | {
      heading: {
        ['aria-labelledby']: string
        title: string
        component: ReactElement<HeadingProps>
      }
    }

export type BaseCardProps = {
  subtitle?: CardSubtitle
  description?: ReactNode
  icon?: CardIconType
  iconBackgroundColor?: string
  iconColor?: string
  textBadge?: React.ReactElement<ColoredLabelProps>
  className?: string
  maxWidth?: number | string
  tagName?: CardTagName
  ['data-testid']?: string
} & TitleProps

interface Title {
  text: string
  id?: string
}

export interface CardTitle extends Title {
  $variant?: 'body' | 'heading'
  $tagName?: 'h2' | 'h3'
}

export type CardSubtitle = Title

export type Variant = 'basic' | 'primary' | 'rich'

export type ClickOptions = WithButtonClick | WithContentClick

interface WithClick {
  kind: 'button' | 'content'
  onClick: UnknownClickHandler | undefined
  ariaDescribedBy?: string
}

export interface WithButtonClick extends WithClick {
  kind: 'button'
  buttonText: string
  disabled?: boolean
  dataTestId?: string
}

export interface WithContentClick extends WithClick {
  kind: 'content'
}

export type ClickOptionKind = 'button' | 'content'

export type UnknownClickHandler = () => void

const BaseCard = styled(SdsCard)<{
  $cardVariant: Variant
  $maxWidth: number | string | undefined
}>`
  max-width: ${({ $maxWidth }) => resolveCssDimensionValue($maxWidth, '500px')};
  min-width: ${(props) => (props.$cardVariant === 'basic' ? 'auto' : '242px')};
  && {
    &:hover {
      box-shadow: 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.16);
    }
  }
  container-type: inline-size;
  color: ${Colors.TextDefaultNeutral};
`

const Content = styled.div<{ $variant: Variant; $cursorPointer: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: ${(props) => (props.$variant === 'basic' ? 'wrap' : 'nowrap')};
  justify-content: space-between;
  gap: ${SPACE_SMALL};
  padding: ${SPACE_SMALL};
  cursor: ${(props) => (props.$cursorPointer ? 'pointer' : 'auto')};
`

export const SHOW_ICON_BREAKPOINT = 343

const IconWrapper = styled.div.attrs<{
  $variant: Variant
  $backgroundColor?: string
  $color?: string
}>({ 'aria-hidden': true })`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? Colors.BackgroundWeakPrimary};
  color: ${({ $color }) => $color ?? Colors.ElementStrongPrimary};
  @container (max-width: ${SHOW_ICON_BREAKPOINT}px) {
    display: ${(props) => (props.$variant === 'basic' ? 'flex' : 'none')};
  }
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
`

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  text-align: left;
`

const TextBadgeWrapper = styled.section`
  span {
    font-weight: 400;
  }
`

const Description = styled.div`
  margin-top: ${SPACE_XXSMALL};
`

const ButtonWrapper = styled.div``

const ChevronRightIconWrapper = styled.div.attrs({ 'aria-hidden': true })`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 60px;
`

const AccessibilityHelper = styled.a`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
`

const Shortcuts = styled.footer``

const ShortcutElementsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${SPACE_SMALL} ${SPACE_SMALL};
`

const ShortcutElementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${SPACE_SMALL};
  a {
    display: flex;
    justify-content: space-between;
    align-items: center;

    span {
      font-size: 24px;
    }
  }
  button {
    @media (min-width: ${BREAKPOINT_DESKTOP_PX}px) {
      width: 100%;
    }
  }
`

export interface LinkProps {
  href: string
  icon: JSX.Element
  onClick?: UnknownClickHandler
  label: string
  disabled?: boolean
  ariaDescribedBy?: string
}

export type CardIconType =
  | 'bankingCredentialsIcon'
  | 'bankingPayslipIcon'
  | 'communicationMessageUnreadIcon'
  | 'controlEditIcon'
  | 'documentEditIcon'
  | 'documentHistoryIcon'
  | 'documentTextIcon'
  | 'hospitalityDiamondIcon'
  | 'sbonusVerticalIcon'
  | 'sokIcon'
  | 'userPasswordIcon'
  | 'userProfileIcon'
  | 'walletIcon'
  | 'bankingAccountIcon'
  | 'bankingStockIcon'
  | 'paymentSlipIcon'

const icons: Record<CardIconType, JSX.Element> = {
  bankingCredentialsIcon: <BankingCredentialsIcon size={IconSize.MEDIUM} />,
  bankingPayslipIcon: <BankingPayslipIcon size={IconSize.MEDIUM} />,
  communicationMessageUnreadIcon: <CommunicationMessageUnreadIcon size={IconSize.MEDIUM} />,
  controlEditIcon: <PenIcon size={IconSize.MEDIUM} />,
  documentEditIcon: <DocumentEditIcon size={IconSize.MEDIUM} />,
  documentHistoryIcon: <DocumentHistoryIcon size={IconSize.MEDIUM} />,
  documentTextIcon: <DocumentTextIcon size={IconSize.MEDIUM} />,
  hospitalityDiamondIcon: <HospitalityDiamondIcon size={IconSize.MEDIUM} />,
  sbonusVerticalIcon: <PaymentSBonusVerticalIcon size={IconSize.MEDIUM} />,
  sokIcon: <SokGreenIcon size={IconSize.MEDIUM} />,
  userPasswordIcon: <UserPasswordIcon size={IconSize.MEDIUM} />,
  userProfileIcon: <UserProfileIcon size={IconSize.MEDIUM} />,
  walletIcon: <PaymentWalletIcon size={IconSize.MEDIUM} />,
  bankingAccountIcon: <BankingAccountIcon size={IconSize.MEDIUM} />,
  bankingStockIcon: <BankingStockIcon size={IconSize.MEDIUM} />,
  paymentSlipIcon: <BankingPayslipIcon size={IconSize.MEDIUM} />,
}

export const CardLink = ({ href, icon, label, onClick, disabled, ariaDescribedBy }: LinkProps): JSX.Element => {
  return (
    <SdsLink
      href={href}
      standalone={true}
      icon={icon}
      onClick={onClick}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled}
    >
      {label}
    </SdsLink>
  )
}

export const Card = (props: CardProps): JSX.Element => {
  const {
    className,
    variant,
    subtitle,
    description,
    icon,
    iconColor,
    iconBackgroundColor,
    textBadge,
    ['data-testid']: dataTestId,
    maxWidth,
    tagName,
  } = props

  const id = useId()
  let ariaLabelledBy: string | undefined
  let headingComponent: ReactElement
  let ariaLabel: string

  if ('title' in props) {
    const title = props.title
    const titleId = title.id ?? `${id}-header`
    ariaLabelledBy = props['aria-labelledby'] ?? titleId
    ariaLabel = title.text
    headingComponent = (
      <QuaternaryHeading
        header={title.text}
        id={titleId}
        weight="medium"
        tagName={title.$tagName}
        additionalText={subtitle?.text}
        additionalTextId={subtitle?.id}
      />
    )
  } else {
    ariaLabelledBy = props.heading['aria-labelledby']
    headingComponent = props.heading.component
    ariaLabel = props.heading.title
  }

  const eventfulContent = variant !== 'basic' && props.clickOptions

  const getContentClickHandler = (): UnknownClickHandler | undefined => {
    if (variant === 'basic') {
      return undefined
    }

    const { clickOptions } = props

    if (!clickOptions || clickOptions.kind === 'button') {
      return undefined
    }

    return clickOptions.onClick
  }

  const contentClickHandler = getContentClickHandler()

  return (
    <BaseCard
      tagName={tagName || 'section'}
      data-testid={dataTestId}
      className={className}
      variant="elevated"
      aria-labelledby={ariaLabelledBy}
      $cardVariant={variant}
      $maxWidth={maxWidth}
    >
      <Content $cursorPointer={!!contentClickHandler} $variant={variant}>
        {icon && (
          <IconWrapper $variant={variant} $backgroundColor={iconBackgroundColor} $color={iconColor}>
            <IconContainer>{icons[icon]}</IconContainer>
          </IconWrapper>
        )}
        <MainContent>
          <>
            {headingComponent}
            {description && <Description>{description}</Description>}
          </>
        </MainContent>
        {textBadge && <TextBadgeWrapper>{textBadge}</TextBadgeWrapper>}
        {eventfulContent &&
          (props.clickOptions?.kind === 'button' ? (
            <ButtonWrapper>
              <PrimaryButton
                $isFullWidth={true}
                onClick={props.clickOptions.onClick}
                disabled={props.clickOptions.disabled}
                data-testid={props.clickOptions.dataTestId}
                label={props.clickOptions.buttonText}
                aria-describedby={props.clickOptions.ariaDescribedBy}
              />
            </ButtonWrapper>
          ) : (
            <>
              <ChevronRightIconWrapper>
                <NavigationChevronRightIcon />
              </ChevronRightIconWrapper>
              {contentClickHandler && (
                <AccessibilityHelper
                  href="#"
                  aria-label={ariaLabel}
                  aria-describedby={props.clickOptions?.ariaDescribedBy}
                  onClick={(e) => {
                    e.preventDefault()
                    contentClickHandler()
                  }}
                />
              )}
            </>
          ))}
      </Content>
      {variant === 'rich' && (
        <Shortcuts>
          <Divider />
          <ShortcutElementsWrapper>
            {props.shortcutElements.map((el) => (
              <ShortcutElementWrapper key={el.key}>{el}</ShortcutElementWrapper>
            ))}
          </ShortcutElementsWrapper>
        </Shortcuts>
      )}
    </BaseCard>
  )
}
